import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiAccountCash, mdiClockOutline, mdiRefresh, mdiCheckCircle, mdiAlertCircle, mdiHelpCircleOutline, mdiHelpCircle } from '@mdi/js';
import { paymentApi } from '../api/payment';
import { organizationApi } from '../api/organization';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/AppHeader';
import EarningsSummary from '../components/finances/EarningsSummary';
import RevenueBreakdown from '../components/finances/RevenueBreakdown';
import PendingPayoutsCard from '../components/finances/PendingPayoutsCard';
import TransactionsTable from '../components/finances/TransactionsTable';
import PayoutHistoryTable from '../components/finances/PayoutHistoryTable';
import FinancesDashboardHelpModal from '../components/FinancesDashboardHelpModal';
import './Common.css';
import './Dashboard.css';
import './header.css';
import './FinancesDashboard.css';

/**
 * FinancesDashboard — the org's financial overview page (all revenue streams:
 * classes, tickets, bookings, payouts), not instructor-specific.
 *
 * Shows earnings summary, pending balance, full transaction history, and payout history.
 * Located at /finances (renamed from the legacy /instructor/finances).
 * Stripe Connect setup has moved to Settings → Payment Account.
 */
function FinancesDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const organisationId = user?.organisationId;
    const [pendingBalances, setPendingBalances] = useState([]);
    const [baseCurrency, setBaseCurrency] = useState('AUD');
    const [transactions, setTransactions] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [earningsSummary, setEarningsSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connectStatus, setConnectStatus] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    const loadConnectStatus = useCallback(async () => {
        if (!organisationId) return;
        try {
            const status = await paymentApi.getConnectStatus(organisationId);
            setConnectStatus(status);
        } catch (err) {
            console.error('Error loading connect status:', err);
        }
    }, [organisationId]);

    const loadFinancialData = useCallback(async () => {
        if (!organisationId) return;
        try {
            setLoading(true);
            setError(null);

            const [balance, transactions, payouts, earningsSummaryData, orgProfile] = await Promise.all([
                paymentApi.getBalance(organisationId),
                paymentApi.getTransactions(organisationId, 100),
                paymentApi.getPayouts(organisationId, 50),
                paymentApi.getEarnings(organisationId),
                organizationApi.getOrgProfile(organisationId).catch(() => null),
            ]);

            // The balance API returns one entry per currency. Keep them all (a travelling
            // instructor can have pending payouts in more than one currency) rather than
            // arbitrarily showing balances[0] — the old behaviour surfaced whichever currency
            // Mongo's $group happened to return first (e.g. a stray NZD test charge).
            setPendingBalances(balance.balances || []);
            if (orgProfile?.currency) setBaseCurrency(orgProfile.currency);
            setTransactions(transactions || []);
            setPayouts(payouts || []);
            if (earningsSummaryData.success) {
                setEarningsSummary(earningsSummaryData);
            }
        } catch (err) {
            console.error('Error loading financial data:', err);
            setError('Failed to load financial data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [organisationId]);

    useEffect(() => {
        loadConnectStatus();
        loadFinancialData();
    }, [loadConnectStatus, loadFinancialData]);

    return (
        <div className="dashboard-container org-console">
            <FinancesDashboardHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
            {/* Standard Navigation Header */}
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    {/* Sub-portal header */}
                    <div className="sub-portal-header-card" style={{ padding: '20px' }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <button
                                onClick={() => navigate('/manage-finances')}
                                className="back-nav-btn">
                                ← Manage Finances
                            </button>
                            <h1
                                className="sub-portal-header-title"
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    margin: 0,
                                    whiteSpace: 'nowrap',
                                }}>
                                Finances
                            </h1>
                            <button
                                className="finances-refresh-btn"
                                onClick={loadFinancialData}
                                disabled={loading}
                                aria-label="Refresh financial data"
                                style={{ marginLeft: 'auto' }}>
                                <Icon path={mdiRefresh} size={0.9} />
                                <span>Refresh</span>
                            </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <p style={{ margin: 0, color: 'var(--dl-color-gray-700)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Icon path={mdiAccountCash} size={0.85} color="var(--dl-color-purple-default)" />
                                    Earnings, transactions and payout history
                                </p>
                                <button
                                    className="classes-help-trigger"
                                    onClick={() => setHelpOpen(true)}
                                    onMouseEnter={() => setHelpIconHovered(true)}
                                    onMouseLeave={() => setHelpIconHovered(false)}
                                    aria-label="Finances Help"
                                    title="How It Works">
                                    <Icon
                                        path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                        size="1.62rem"
                                        color="var(--dl-color-purple-default)"
                                    />
                                </button>
                            </div>
                            {connectStatus && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {connectStatus.status === 'active' ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '20px', padding: '3px 10px' }}>
                                            <Icon path={mdiCheckCircle} size={0.55} />
                                            Payment account connected
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '20px', padding: '3px 10px' }}>
                                            <Icon path={mdiAlertCircle} size={0.55} />
                                            Payout account not set up
                                        </span>
                                    )}
                                    <button
                                        onClick={() => navigate('/settings')}
                                        style={{ fontSize: '12px', color: '#451f78', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                                        Payment settings →
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="finances-loading">
                            <Icon path={mdiClockOutline} size={1} />
                            <span>Loading financial data...</span>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="finances-error-card">
                            <p>{error}</p>
                            <button className="finances-retry-btn" onClick={loadFinancialData}>
                                Try Again
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {/* Earnings summary — all-time, this month, last month */}
                            <EarningsSummary summary={earningsSummary} />

                            {/* Overview cards */}
                            <div className="finances-overview">
                                <RevenueBreakdown transactions={transactions} currency={earningsSummary?.currency} />
                                <PendingPayoutsCard balances={pendingBalances} baseCurrency={baseCurrency} />
                            </div>

                            {/* Transaction history */}
                            <div className="content-card finances-section">
                                <TransactionsTable
                                    transactions={transactions}
                                    organisationId={organisationId}
                                    onRefresh={loadFinancialData}
                                />
                            </div>

                            {/* Payout history */}
                            <div className="content-card finances-section">
                                <h2 className="finances-section-title">Payout History</h2>
                                <PayoutHistoryTable
                                    payouts={payouts}
                                    organisationId={organisationId}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FinancesDashboard;
